// packages/charts/src/timeAxis.ts
//
// The time-axis contract (ADR-0266): a time-series spec may carry
// `time_axis` — the period the viewer asked for, stamped by the backend
// chokepoint (services/chart_formatter.py) after it laid the rows onto that
// period's bucket axis. `end` is where the axis stops, `through` the last day
// own data may be expected (yesterday in the tenant's timezone, or later when
// a row already exists). Everything after `through` is future: bare, with
// only the comparison drawn there. A bucket at or before `through` with no
// value is BEHIND: a gap, under the amber "not filled in yet" zone.
//
// This module is the one derivation of "which comparison bucket belongs to
// this main bucket" — the period table (`table/alignComparisonByDate.ts`)
// and the series merge (apps/frontend/src/utils/comparisonAlignment.ts) both
// read it, so the two surfaces cannot pair differently.
//
// Pure: no React, no environment reads. The app passes `warn`.

import {
  addDays,
  differenceInCalendarDays,
  parseISODate,
  startOfISOWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMonths,
  subYears,
} from "./table/calendar";
import type { TimeGrain } from "./table/formatDateValue";
import type { TimeAxis } from "./types";

export type { TimeAxis };

export const TIME_AXIS_GRAINS: readonly TimeGrain[] = [
  "daily",
  "weekly",
  "monthly",
  "quarterly",
  "yearly",
];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isIsoDate(value: unknown): value is string {
  if (typeof value !== "string" || !ISO_DATE.test(value)) return false;
  return !Number.isNaN(parseISODate(value).getTime());
}

/**
 * The `time_axis` on a spec, or null when it is absent or malformed. A
 * malformed stamp (a persisted spec edited by hand, a field from another
 * build) must never steer the renderer — the review's security finding: an
 * unvalidated stamp reaching the mailed report could paint a false zone.
 * Shape: four fields, three ISO dates with `start <= end` and `through <= end`,
 * a known grain.
 */
export function readTimeAxis(spec: Record<string, unknown> | null | undefined): TimeAxis | null {
  const raw = spec?.time_axis;
  if (!raw || typeof raw !== "object") return null;
  const { start, end, through, grain } = raw as Record<string, unknown>;
  if (!isIsoDate(start) || !isIsoDate(end) || !isIsoDate(through)) return null;
  if (start > end || through > end) return null;
  if (!TIME_AXIS_GRAINS.includes(grain as TimeGrain)) return null;
  return { start, end, through, grain: grain as TimeGrain };
}

function isFiniteValue(value: unknown): boolean {
  if (typeof value === "number") return Number.isFinite(value);
  // Some sources emit numeric strings ("12.5"); an empty/blank string must
  // not count — `Number("")` and `Number("  ")` are both 0.
  if (typeof value === "string" && value.trim() !== "") {
    return Number.isFinite(Number(value));
  }
  return false;
}

function rowsHaveSignal(rows: unknown, yKeys: readonly string[]): boolean {
  if (!Array.isArray(rows)) return false;
  return rows.some((row) => {
    if (!row || typeof row !== "object") return false;
    const record = row as Record<string, unknown>;
    return yKeys.some((key) => isFiniteValue(record[key]));
  });
}

/**
 * Whether an axis-stamped spec has anything to draw: at least one finite
 * y-value, own or comparison. Gates a cartesian tile into an empty state
 * instead of a blank chart when the backend has laid an empty period onto
 * the full bucket range (ADR-0266) — every bucket present, every y-key
 * null, `rows.length` no longer zero.
 *
 * Conservative in every direction that could hide a chart the reader should
 * see:
 * - No `time_axis` stamp -> true. The gate is scoped to axis-stamped specs;
 *   anything else keeps today's behaviour untouched.
 * - No `y_keys` -> true. Nothing to judge a value against, so never hide on
 *   ignorance.
 * - `comparison_rows` counts only in its series shape (an array parallel to
 *   `rows`); a table's keyed Record is read as "no comparison" rather than
 *   risking a false negative on an unfamiliar shape.
 */
export function timeAxisHasSignal(
  spec:
    | (Record<string, unknown> & {
        y_keys?: string[];
        rows?: unknown;
        comparison_rows?: unknown;
      })
    | null
    | undefined,
): boolean {
  if (!readTimeAxis(spec)) return true;
  const yKeys = spec?.y_keys;
  if (!yKeys || yKeys.length === 0) return true;
  return rowsHaveSignal(spec?.rows, yKeys) || rowsHaveSignal(spec?.comparison_rows, yKeys);
}

/** Local-calendar ISO key for a local-midnight date (never toISOString —
 *  that shifts a local date across the UTC boundary east of Greenwich). */
export function isoKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function truncateToGrain(date: Date, grain: TimeGrain): Date {
  switch (grain) {
    case "weekly":
      return startOfISOWeek(date);
    case "monthly":
      return startOfMonth(date);
    case "quarterly":
      return startOfQuarter(date);
    case "yearly":
      return startOfYear(date);
    default:
      return date;
  }
}

/** Exclusive end of the bucket that starts at `bucketStart`. */
export function nextBucketStart(bucketStart: Date, grain: TimeGrain): Date {
  switch (grain) {
    case "weekly":
      return addDays(bucketStart, 7);
    case "monthly":
      return startOfMonth(addDays(bucketStart, 32));
    case "quarterly":
      return startOfQuarter(addDays(bucketStart, 95));
    case "yearly":
      return startOfYear(addDays(bucketStart, 370));
    default:
      return addDays(bucketStart, 1);
  }
}

export interface AxisRange {
  /** ISO date, inclusive. */
  start: string;
  /** ISO date, inclusive. */
  end: string;
}

/**
 * How a main bucket finds its comparison bucket: a calendar-year step back
 * (`previous_year` on month/quarter/year periods, so February pairs with
 * February across leap years) or a fixed day offset (everything else,
 * including the weekday-aligned `previous_year` of day/week periods, whose
 * 364-day shift keeps Monday on Monday).
 */
export type ComparisonShift =
  | { kind: "calendar_year" }
  | { kind: "days"; offset: number }
  /** A calendar previous-period leg (plan 2026-10-11 dashboard period scope,
   *  B2): each bucket `months` back, clamped (31 Mar -> 28 Feb) — never a day
   *  count, which drifts across month ends. Twin of `time_buckets.py`. */
  | { kind: "calendar_months"; months: number };

const isoDay = (d: Date) => `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;

/**
 * The month shift of a calendar previous-period leg, or null — twin of
 * `period_axis.py::calendar_unit_shift` restricted to month units (a week shift
 * is a plain 7-day offset). Both ends must match the calendar rule, so a
 * hand-picked equal-length window that merely starts a month back is not
 * misread as one.
 */
function calendarMonthShift(mainRange: AxisRange, comparisonRange: AxisRange): number | null {
  const mainStart = parseISODate(mainRange.start);
  const mainEnd = parseISODate(mainRange.end);
  const compStart = parseISODate(comparisonRange.start);
  const compEnd = parseISODate(comparisonRange.end);
  if ([mainStart, mainEnd, compStart, compEnd].some((d) => Number.isNaN(d.getTime()))) return null;
  if (mainStart.getDate() !== 1) return null;
  for (const months of [1, 3, 12]) {
    if (isoDay(compStart) !== isoDay(subMonths(mainStart, months))) continue;
    if (isoDay(compEnd) === isoDay(subMonths(mainEnd, months))) return months;
    const wholeUnit = isoDay(mainEnd) === isoDay(addDays(subMonths(mainStart, -months), -1));
    if (wholeUnit && isoDay(compEnd) === isoDay(addDays(mainStart, -1))) return months;
  }
  return null;
}

/**
 * Decide the shift ONCE per pairing from the two windows. Which of the two
 * `previous_year` alignments produced the comparison window is readable from
 * the offset itself: a calendar year is 365 or 366 days and neither is
 * divisible by 7, while weekday alignment is -364 = 52 whole weeks. `< 0`
 * excludes the degenerate offset 0, which would pair every bucket with
 * itself.
 *
 * INVARIANT: weekday alignment and month/quarter/year grain are mutually
 * exclusive (the weekday-aligned presets are day and week periods). At those
 * grains the two branches converge today — bucket starts are period starts
 * — so this falls back to the calendar branch and warns rather than relying
 * on that accident. Nothing observable changes; the warning is the signal.
 */
export function comparisonShift(opts: {
  grain: TimeGrain;
  comparisonMode: string;
  mainRange: AxisRange;
  comparisonRange: AxisRange;
  warn?: (message: string) => void;
}): ComparisonShift {
  const { grain, comparisonMode, mainRange, comparisonRange, warn } = opts;
  const dayOffset = differenceInCalendarDays(
    parseISODate(comparisonRange.start),
    parseISODate(mainRange.start),
  );
  const grainIsCalendarAnchored =
    grain === "monthly" || grain === "quarterly" || grain === "yearly";
  const looksWeekdayAligned =
    comparisonMode === "previous_year" && dayOffset < 0 && dayOffset % 7 === 0;

  if (warn && looksWeekdayAligned && grainIsCalendarAnchored) {
    warn(
      `[alignComparisonByDate] weekday-aligned window (${dayOffset}d) at `
      + `${grain} grain — the invariant says these cannot co-occur. `
      + "Falling back to the calendar shift.",
    );
  }
  if (comparisonMode === "previous_year" && !(looksWeekdayAligned && !grainIsCalendarAnchored)) {
    return { kind: "calendar_year" };
  }
  if (comparisonMode !== "previous_year" && comparisonMode !== "same_period_last_year") {
    const months = calendarMonthShift(mainRange, comparisonRange);
    if (months !== null) return { kind: "calendar_months", months };
  }
  return { kind: "days", offset: dayOffset };
}

/** The comparison bucket for `bucketStart` under `shift`, re-truncated to the grain. */
export function counterpartBucketStart(
  bucketStart: Date,
  shift: ComparisonShift,
  grain: TimeGrain,
): Date {
  const shifted =
    shift.kind === "calendar_year"
      ? subYears(bucketStart, 1)
      : shift.kind === "calendar_months"
        ? subMonths(bucketStart, shift.months)
        : addDays(bucketStart, shift.offset);
  return truncateToGrain(shifted, grain);
}

function toDate(value: unknown): Date | null {
  if (value == null) return null;
  const iso = String(value).slice(0, 10);
  if (!ISO_DATE.test(iso)) return null;
  const parsed = parseISODate(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * One comparison row per main row, paired BY DATE: each main bucket finds its
 * counterpart through `comparisonShift`, never through its position. A main
 * bucket without a counterpart gets a row carrying only the counterpart's
 * index value — every metric absent, so the renderer draws a gap there, not
 * a zero. Immune to count mismatch and to gaps in either series by
 * construction; the positional pairing it replaces desynchronised every
 * bucket after the first hole.
 */
export function pairComparisonByDate(opts: {
  mainRows: Record<string, unknown>[];
  comparisonRows: Record<string, unknown>[];
  indexKey: string;
  grain: TimeGrain;
  comparisonMode: string;
  mainRange: AxisRange;
  comparisonRange: AxisRange;
  warn?: (message: string) => void;
}): Record<string, unknown>[] {
  const { mainRows, comparisonRows, indexKey, grain } = opts;
  const shift = comparisonShift(opts);

  const compByBucket = new Map<string, Record<string, unknown>>();
  for (const row of comparisonRows) {
    const date = toDate(row[indexKey]);
    if (date) compByBucket.set(isoKey(truncateToGrain(date, grain)), row);
  }

  return mainRows.map((row) => {
    const bucketStart = toDate(row[indexKey]);
    if (!bucketStart) return {};
    const counterpart = isoKey(counterpartBucketStart(bucketStart, shift, grain));
    return compByBucket.get(counterpart) ?? { [indexKey]: counterpart };
  });
}
